import type { ToastItem } from '../hooks/useToast';

/** 统一轻提示：底部 16px + 安全区，带可选“重试”动作。 */
export function ToastHost({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return <div className="toast-host" role="status" aria-live="polite">
    {toasts.map((toast) => <div key={toast.id} className={'toast toast-' + toast.tone}>
      <span>{toast.message}</span>
      {toast.action && <button type="button" onClick={() => { toast.action?.run(); onDismiss(toast.id); }}>{toast.action.label}</button>}
      <button type="button" className="toast-close" aria-label="关闭提示" onClick={() => onDismiss(toast.id)}>×</button>
    </div>)}
  </div>;
}

/** 下拉刷新指示器：跟随手指位移，松手后展示“正在刷新”。 */
export function PullIndicator({ distance, refreshing }: { distance: number; refreshing: boolean }) {
  const visible = distance > 0 || refreshing;
  return <div className={'pull-indicator' + (visible ? ' visible' : '')} style={{ transform: 'translateY(' + (refreshing ? 44 : distance) + 'px)' }} aria-hidden={!visible}>
    <span className={'pull-spinner' + (refreshing ? ' spinning' : '')} />
    <span>{refreshing ? '正在刷新…' : distance >= 72 ? '松手立即刷新' : '下拉刷新'}</span>
  </div>;
}

/** 首屏骨架屏：3 张卡片占位，避免空白等待。 */
export function SkeletonList({ count = 3 }: { count?: number }) {
  return <div className="match-list skeleton-list" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => <div className="match-card skeleton-card" key={index}>
      <div className="skeleton-line w60" />
      <div className="skeleton-line w90 tall" />
      <div className="skeleton-block" />
      <div className="skeleton-block" />
    </div>)}
  </div>;
}

export function SkeletonChrome() {
  return <div className="skeleton-chrome" aria-hidden="true">
    <div className="skeleton-line w40" />
    <div className="skeleton-block" />
  </div>;
}

interface EmptyStateProps { title: string; hint?: string; actionLabel?: string; onAction?: () => void }
export function EmptyState({ title, hint, actionLabel, onAction }: EmptyStateProps) {
  return <div className="state-box">
    <b>{title}</b>
    {hint && <span>{hint}</span>}
    {actionLabel && onAction && <button type="button" className="state-action" onClick={onAction}>{actionLabel}</button>}
  </div>;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="state-box error-box">
    <b>暂时无法加载</b>
    <span>{message}</span>
    <button type="button" className="state-action" onClick={onRetry}>重试</button>
  </div>;
}

interface ConfirmDialogProps { open: boolean; title: string; body: string; confirmLabel?: string; onConfirm: () => void; onCancel: () => void }
/** 危险操作二次确认（吊销 Token、清空串关等）。 */
export function ConfirmDialog({ open, title, body, confirmLabel = '确认', onConfirm, onCancel }: ConfirmDialogProps) {
  if (!open) return null;
  return <div className="confirm-backdrop" role="dialog" aria-modal="true" aria-label={title} onClick={onCancel}>
    <div className="confirm-card" onClick={(event) => event.stopPropagation()}>
      <h3>{title}</h3>
      <p>{body}</p>
      <div className="confirm-actions">
        <button type="button" className="ghost" onClick={onCancel}>取消</button>
        <button type="button" className="danger" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  </div>;
}
